import { InformationCircleIcon } from "@heroicons/react/20/solid";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Button } from "@/atoms-react/button/Button.react";
import { Base } from "@/atoms-react/text/Text.react";
import { MiniAnimatedShield } from "@/components/animations/minishield/index.react";
import {
  AgentNatureMandate,
  LegalEntityLinkDto,
  MandateService,
  RCSRepresentative
} from "@/features/company/types";
import { StaticNatureMandate } from "@/features/user/types";
import { RepresentativeConfiguration } from "../RepresentativeConfiguration";

type AddCompanyRepresentativeFormPrps = {
  onValidate: (data: LegalEntityLinkDto) => void;
  isLoadingCreation: boolean;
  natureMandates?: StaticNatureMandate[];
  representative?: LegalEntityLinkDto | null;
  pendingNatureMandates?: boolean;
  representativeRcs: RCSRepresentative | null;
};
export const RepresentativeForm = ({
  onValidate,
  isLoadingCreation,
  representative,
  natureMandates,
  pendingNatureMandates,
  representativeRcs
}: AddCompanyRepresentativeFormPrps) => {
  const { t } = useTranslation(["pass"]);

  const methods = useForm<LegalEntityLinkDto>({
    defaultValues: {
      mandatoryInformation: {
        agentNatureRelation:
          representative && representative.mandatoryInformation.agentNatureRelation
            ? AgentNatureMandate[representative.mandatoryInformation.agentNatureRelation]
            : representativeRcs && representativeRcs?.agent_nature_relation
              ? AgentNatureMandate[representativeRcs.agent_nature_relation]
              : undefined,
        agentNatureRelationOther: representative
          ? representative?.mandatoryInformation.agentNatureRelationOther
          : representativeRcs
            ? representativeRcs.agent_nature_relation_other
            : undefined,
        mandateServices: [MandateService.ALIASES_MANAGEMENT],
        isAgentLe: undefined,
        isCommunicationContact: true
      }
    },
    mode: "all"
  });
  const { handleSubmit } = methods;
  const onSubmit: SubmitHandler<LegalEntityLinkDto> = (data) => {
    onValidate(data);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mt-5 flex flex-col justify-between border-2 border-slate-50 p-4 dark:border-slate-700">
          <div className="flex flex-row">
            <div
              className={twMerge(
                `flex w-full flex-start justify-center rounded-sm bg-bottom bg-cover md:w-1/5 lg:px-5`,
                "h-[100px] bg-[url('/images/olkypass/declarative_kyc_bg.png')]"
              )}
            >
              <div className={"flex flex-col justify-center"}>
                {
                  <MiniAnimatedShield
                    style="bottom-14 left-4 w-5 h-3"
                    colors="#D11E1F"
                    percentage={45}
                  />
                }
              </div>
            </div>
            <div className="mt-2 flex w-full w-full flex-row content-center justify-center md:mt-0 md:ml-4 md:flex-col">
              <div className="flex flex-row whitespace-nowrap">
                <Base className="font-semibold text-base text-slate-500">
                  {t("olkypass.mainPage.olkypass.levelPage.status_declarative")}
                </Base>
                <InformationCircleIcon
                  className="ml-2 h-5 w-5 rounded-full bg-primary text-white dark:text-slate-900"
                  data-tooltip={t(
                    "olkypass.mainPage.olkypass.levelPage.declarative_sub_description"
                  )}
                  data-position="bottom"
                />
              </div>
            </div>
          </div>
          <RepresentativeConfiguration
            pendingNatureMandates={pendingNatureMandates}
            natureMandates={natureMandates}
            representative={representative}
          />
          <div className="mt-8 flex w-full flex-row justify-center">
            <Button
              size="md"
              loading={isLoadingCreation}
              type="submit"
              className="w-1/5 rounded-sm bg-primary"
            >
              {t("business.label.validate")}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
